import PropTypes from 'prop-types';
import {
  Grid,
  Typography,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { useState, useEffect, useCallback } from 'react';
import { useWatch } from 'react-hook-form';

import {
  FormTextField,
  FormLearnerInfoFields,
  FormRadioGroup,
} from '~common/components';
import { dataLists } from '~common/utils';
import checkEmailExists from '@patheducation/admin/src/utils/checkEmail';
import MuiPhone from '~common/components/Phone/MuiPhone.jsx';
import useStyles from './styles';

const { countryCodeOptions } = dataLists;

// Email regex pattern
const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~\-]+(\.[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~\-]+)*@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;

// Capitalize first letter of each word
const capitalizeWords = (str) => str.trim().replace(/\b\w/g, (char) => char.toUpperCase());

const ParentLearnerFields = ({
  control,
  getValues,
  setValue,
  setError,
  clearErrors,
  fieldState,
  watch,
  type,
  trigger,
  formState,
}) => {
  const { classes } = useStyles();
  const isNewLearner = type === 'parentLearnerCreate';
  const [noContactDetails, setNoContactDetails] = useState(getValues('noDetails') || false);
  const [emailExists, setEmailExists] = useState(false);
  const [phoneNumberError, setPhoneNumberError] = useState(false);

  const email = useWatch({
    control,
    name: 'emailAddress',
  });

  useEffect(() => {
    if (formState?.isSubmitted && !noContactDetails) {
      const currentNumber = getValues('phoneNumber');
      setPhoneNumberError(!currentNumber);
    }
  }, [formState?.isSubmitted, formState?.submitCount, noContactDetails, getValues]);

  const isValidEmail = (emailValue) => emailRegex.test(emailValue);

  const debouncedCheckEmail = useCallback(
    (() => {
      let timer;
      return (emailValue) => {
        clearTimeout(timer);
        timer = setTimeout(async () => {
          // We run the check if it's a new learner OR if the email field is enabled for editing
          const shouldCheckEmail = isNewLearner || fieldState?.emailDisabled === false;

          if (shouldCheckEmail && emailValue && isValidEmail(emailValue)) {
            try {
              const exists = await checkEmailExists(emailValue);
              setEmailExists(exists);
              if (exists) {
                setError('emailAddress', {
                  type: 'manual',
                  message: 'This email is already in use',
                });
              } else {
                clearErrors('emailAddress');
              }
            } catch (error) {
              console.error('Error checking email:', error);
              setError('emailAddress', {
                type: 'manual',
                message: 'Error checking email availability',
              });
            }
          } else if (shouldCheckEmail && emailValue) {
            setError('emailAddress', {
              type: 'manual',
              message: 'Please enter a valid email address',
            });
          } else {
            clearErrors('emailAddress');
          }
        }, 500);
      };
    })(),
    [setError, clearErrors, isNewLearner, fieldState?.emailDisabled], // Added dependency
  );

  useEffect(() => {
    // Check if email ends with '@patheducation.co.uk' and clear it
    if (email && email.endsWith('@patheducation.co.uk')) {
      setValue('emailAddress', '');
      return;
    }

    // We should check the email if:
    // 1. We are not in the "no contact details" state
    // 2. AND (it's a new learner OR the email field is enabled for editing)
    const shouldCheckEmail = !noContactDetails && (isNewLearner || fieldState?.emailDisabled === false);

    if (shouldCheckEmail) {
      debouncedCheckEmail(email);
    } else {
      clearErrors('emailAddress');
      setEmailExists(false);
    }
  }, [email, debouncedCheckEmail, noContactDetails, clearErrors, isNewLearner, fieldState?.emailDisabled, setValue]); // Added dependency

  useEffect(() => {
    if (noContactDetails) {
      if (isNewLearner) {
        setValue('emailAddress', '');
        setValue('phoneNumber', 0);
        setValue('phoneNumberPrefix', { });
        setValue('ownAccount', true);
      }
      setValue('noDetails', true);
      clearErrors('emailAddress');
      clearErrors('phoneNumber');
      setPhoneNumberError(false);
    } else {
      // User is un-checking the box (i.e., adding details)
      // eslint-disable-next-line no-lonely-if
      setValue('noDetails', false);
      // Don't reset ownAccount if they are just unchecking the box
    }
  }, [noContactDetails, setValue, clearErrors, isNewLearner]);

  useEffect(() => {
    if (!isNewLearner && getValues('noDetails') === true) {
      setNoContactDetails(true);
    }
  }, [isNewLearner, getValues]);

  // Extract current prefix and number values correctly
  const currentPrefixObj = getValues('phoneNumberPrefix');
  const currentPrefix = (currentPrefixObj && currentPrefixObj.value) || '';
  const currentNumber = getValues('phoneNumber') || '';
  // Determine default country for PhoneInput based on stored prefix object
  // Fallback to UK ('gb') if not found or invalid to avoid defaulting to 'us'
  const defaultCountry = currentPrefixObj && currentPrefixObj.id
    ? currentPrefixObj.id.toLowerCase()
    : 'gb';

  return (
    <Grid container columnSpacing={1} style={{ marginTop: 5, marginBottom: 5 }}>
      <Grid item xs={12} sm={6}>
        <FormTextField
          name="firstName"
          control={control}
          inputProps={{
            label: 'First Name *',
            autoFocus: true,
            autoComplete: 'off',
            onBlur: (e) => setValue(
              'firstName',
              capitalizeWords(e.target.value),
            ),
          }}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormTextField
          name="lastName"
          control={control}
          inputProps={{
            label: 'Last Name *',
            onBlur: (e) => setValue(
              'lastName',
              capitalizeWords(e.target.value),
            ),
          }}
        />
      </Grid>

      {!fieldState?.noDetailsDisabled && (
      <Grid item xs={12}>
        <FormControlLabel
          control={(
            <Checkbox
              checked={noContactDetails}
              onChange={(e) => setNoContactDetails(e.target.checked)}
              color="primary"
              disabled={fieldState?.noDetailsDisabled}
            />
          )}
          label="The learner does not have contact details"
        />
      </Grid>
      )}

      {(!noContactDetails) && (
        <>
          <Grid item xs={12}>
            <FormTextField
              name="emailAddress"
              control={control}
              inputProps={{
                label: 'Email Address *',
                disabled: fieldState?.emailDisabled,
                readOnly: fieldState?.emailDisabled,
              }}
              // Show error if emailExists is true AND (it's a new learner OR the field is editable)
              error={emailExists && (isNewLearner || fieldState?.emailDisabled === false)}
              helperText={emailExists && (isNewLearner || fieldState?.emailDisabled === false) ? 'This email is already in use' : ''}
            />
            {!isNewLearner && fieldState?.emailDisabled && (
              <Typography variant="body2" className={classes.message}>
                Please note: If you need to update your email address please contact an administrator.
              </Typography>
            )}
          </Grid>
          {!fieldState?.ownAccountDisabled && (
          <Grid item xs={12}>
            <FormRadioGroup
              name="ownAccount"
              control={control}
              label="Would you like the student to have access to their own account using their own log in details?"
              disabled={fieldState?.ownAccountDisabled}
            />
          </Grid>
          )}
          <Grid item xs={12}>
            <MuiPhone
              country={defaultCountry}
              error={phoneNumberError}
              value={`+${currentPrefix}${currentNumber}`}
              containerStyle={{ marginBottom: '5px', marginTop: '10px', color: 'grey' }}
              inputStyle={{ marginBottom: '5px', marginTop: '10px' }}
              onChange={(data) => {
                setPhoneNumberError(!noContactDetails && !data.phone);
                const dialCode = `+${data.country.dialCode}`;
                // Determine correct country based on library provided country code
                let matchedOption = null;
                if (data.country.iso2) {
                  matchedOption = countryCodeOptions.find(
                    (option) => option.id.toLowerCase() === data.country.iso2.toLowerCase(),
                  );
                }
                // Fallback if country match not found using id, try matching dial code
                if (!matchedOption) {
                  matchedOption = countryCodeOptions.find(
                    (option) => option.value === dialCode,
                  );
                }
                if (matchedOption) {
                  setValue('phoneNumberPrefix', matchedOption);
                } else {
                  setValue('phoneNumberPrefix', { name: '', value: dialCode, id: '' });
                }
                // Remove prefix from value
                let numberWithoutPrefix = data.phone.replace(dialCode, '');
                // Remove any leading zeros if necessary
                numberWithoutPrefix = numberWithoutPrefix.replace(/^0+/, '');
                setValue('phoneNumber', numberWithoutPrefix);
              }}
              inputProps={{
                name: 'phone',
                required: true,
              }}
            />
          </Grid>
        </>
      )}

      <Grid item xs={12}>
        <FormLearnerInfoFields
          type={type}
          control={control}
          setValue={setValue}
          getValues={getValues}
          watch={watch}
          trigger={trigger}
        />
      </Grid>
    </Grid>
  );
};

ParentLearnerFields.defaultProps = {
  getValues: null,
  setValue: null,
  clearErrors: () => {},
  setError: () => {},
  trigger: null,
  fieldState: {},
  formState: {},
};

ParentLearnerFields.propTypes = {
  control: PropTypes.shape({}).isRequired,
  getValues: PropTypes.func,
  setValue: PropTypes.func,
  clearErrors: PropTypes.func,
  setError: PropTypes.func,
  formState: PropTypes.shape({}),
  fieldState: PropTypes.shape({}),
  trigger: PropTypes.func,
  watch: PropTypes.func.isRequired,
  type: PropTypes.oneOf(['parentLearnerCreate', 'parentLearnerEdit']).isRequired,
};

export default ParentLearnerFields;
